import { useContext, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import clsx from 'clsx';
import * as R from 'ramda';

import {
  exceedsADOTStock,
  toADOTPositiveQuantity,
} from '@atom/components/common/requests/adotQuantityUtilities';
import { getApprovalKeyword } from '@atom/components/common/requests/customTenantUtilities';
import { GET_ASSET_USAGE_SUMMARY } from '@atom/graph/asset';
import { Button, Progress, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AssetRequestStatus } from '@atom/types/assetRequest';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import ViewRequestContext from '../ViewRequestContext';

import classes from './adotView.module.css';

const styles = {
  label: {
    color: colors.neutral.dim,
  },
  cancelButton: {
    marginRight: '1rem',
  },
};

const ADOTApprovalForm = () => {
  const {
    fromAsset,
    activeRequest,
    quantityApproved,
    setQuantityApproved,
    comments,
    setComments,
    status,
    setStatus,
    setConfirmOpen,
  } = useContext(ViewRequestContext);

  const isApproval =
    status === AssetRequestStatus.FULL_APPROVAL ||
    status === AssetRequestStatus.PARTIAL_APPROVAL;

  const [quantityText, setQuantityText] = useState<string>(
    quantityApproved?.toString() ?? '',
  );

  const { data: usageData, loading: fromTotalRemainingLoading } = useQuery(
    GET_ASSET_USAGE_SUMMARY,
    {
      variables: { assetId: fromAsset?.id },
      fetchPolicy: 'no-cache',
      skip: !fromAsset?.id || !isApproval,
    },
  );

  const fromTotalRemaining = R.pathOr(
    0,
    ['assetUsageSummary', 'totalRemaining'],
    usageData,
  );
  const unit = activeRequest?.assetUnit || '';

  const resetForm = () => {
    setQuantityApproved(null);
    setComments(null);
    setStatus(null);
  };

  const quantityUpdate = (value: string) => {
    const text = value.replace('-', '');
    const updatedValue = toADOTPositiveQuantity(text);

    setQuantityText(text);

    setStatus(
      updatedValue >= activeRequest?.quantityOrdered
        ? AssetRequestStatus.FULL_APPROVAL
        : AssetRequestStatus.PARTIAL_APPROVAL,
    );
    setQuantityApproved(updatedValue);
  };

  const availableStockError =
    !fromTotalRemainingLoading &&
    exceedsADOTStock(fromTotalRemaining, quantityApproved);
  const requestedQuantityError = exceedsADOTStock(
    activeRequest?.quantityOrdered,
    quantityApproved,
  );

  const getQuantityHelperText = () => {
    switch (true) {
      case fromTotalRemainingLoading:
        return <Progress size={10} />;
      case availableStockError:
        return `${fromTotalRemaining} ${unit} in stock. Exceeds available stock levels.`;
      case requestedQuantityError:
        return 'Cannot approve more than the quantity requested.';
      default:
        return `${fromTotalRemaining} ${unit} in stock`;
    }
  };

  const approvalKeyword = getApprovalKeyword(status);
  const cancelText =
    status === AssetRequestStatus.CANCELED ? 'Do Not Cancel' : 'Cancel';
  const confirmText =
    status === AssetRequestStatus.CANCELED ? 'Cancel Request' : approvalKeyword;
  const buttonColor = isApproval ? 'primary' : 'error';
  const disableConfirm =
    isApproval &&
    (fromTotalRemainingLoading ||
      isNilOrEmpty(quantityApproved) ||
      availableStockError ||
      requestedQuantityError);

  return (
    <div className={classes.approvalForm} data-cy="adotTransferApprovalForm">
      <div className={classes.formTitle}>{`${approvalKeyword} Request`}</div>
      {isApproval && (
        <div className={clsx(classes.inputRow, classes.quantity)}>
          <TextField
            slotProps={{ inputLabel: { style: styles.label } }}
            type="number"
            value={quantityText}
            label={`Approved Quantity (${unit})`}
            onChange={event => quantityUpdate(event.target.value)}
            onBlur={() => setQuantityText(quantityApproved?.toString() ?? '')}
            helperText={getQuantityHelperText()}
            error={requestedQuantityError || availableStockError}
            data-cy="adotTransferApprovedQuantity"
          />
        </div>
      )}
      <div className={classes.inputRow}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          fullWidth
          value={comments || ''}
          label="Comments (optional)"
          onChange={event => setComments(event.target.value)}
          data-cy="adotTransferApprovalComments"
        />
      </div>
      <div className={classes.approveButtons}>
        <Button
          sx={styles.cancelButton}
          onClick={resetForm}
          data-cy="adotTransferApprovalBackButton"
        >
          {cancelText}
        </Button>
        <Button
          color={buttonColor}
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          disabled={disableConfirm}
          data-cy="adotTransferApprovalConfirmButton"
        >
          {confirmText}
        </Button>
      </div>
    </div>
  );
};

export default ADOTApprovalForm;
